// One shop sensor: the temperature big, its state in plain words, the last reading, the 24 h low /
// high and the limits. Shared by the desktop /sensors page and the phone screen. Words:
// features/sensors/sensorRules.js. Calm green for Normal, blue for too cold, red for too hot, amber
// when the board or its probe is not reading (the TONE pattern of pages/DoorsPage.jsx).

import React from 'react';
import {
  alertSettingsText,
  fmtTemp,
  lastReadingText,
  limitsText,
  sensorStatus,
} from '../../features/sensors/sensorRules';

export const SENSOR_TONE = {
  ok: { bg: '#e3f2e8', fg: '#1d4d2f', border: '#4f9a6a', temp: 'var(--bs-body-color)' },
  cold: { bg: '#dbeafe', fg: '#1e3a8a', border: '#2563eb', temp: '#2563eb' },
  hot: { bg: '#fde2e1', fg: '#7f1d1d', border: '#dc2626', temp: '#dc2626' },
  offline: { bg: '#fff1cc', fg: '#713f12', border: '#d97706', temp: 'var(--bs-secondary-color)' },
  probe: { bg: '#fff1cc', fg: '#713f12', border: '#d97706', temp: 'var(--bs-secondary-color)' },
  muted: { bg: '#e9ecef', fg: '#495057', border: '#adb5bd', temp: 'var(--bs-secondary-color)' },
};

const styles = {
  card: (tone, selected) => ({
    borderLeft: `6px solid ${SENSOR_TONE[tone].border}`,
    boxShadow: selected ? `0 0 0 2px ${SENSOR_TONE[tone].border}` : undefined,
    textAlign: 'left',
    width: '100%',
  }),
  top: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 },
  name: { fontWeight: 700, fontSize: 17, lineHeight: 1.2 },
  where: { fontSize: 13 },
  temp: (tone) => ({ fontSize: 48, fontWeight: 700, lineHeight: 1.05, color: SENSOR_TONE[tone].temp, letterSpacing: '-0.02em' }),
  badge: (tone) => ({
    display: 'inline-block', background: SENSOR_TONE[tone].bg, color: SENSOR_TONE[tone].fg,
    border: `1px solid ${SENSOR_TONE[tone].border}`, borderRadius: 6, padding: '2px 10px', fontWeight: 700, fontSize: 14,
  }),
  small: { fontSize: 13 },
  detail: (tone) => ({
    fontSize: 13,
    color: tone === 'ok' || tone === 'muted' ? 'var(--bs-secondary-color)' : SENSOR_TONE[tone].border,
  }),
  grid: { display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 10, rowGap: 2, fontSize: 13, marginTop: 8 },
  dt: { color: 'var(--bs-secondary-color)' },
};

export default function SensorCard({ sensor, selected = false, onSelect, now }) {
  const st = sensorStatus(sensor, now);
  const lowHigh = sensor.min_24h == null && sensor.max_24h == null
    ? 'no readings'
    : `${fmtTemp(sensor.min_24h)} / ${fmtTemp(sensor.max_24h)}`;
  const settings = alertSettingsText(sensor);
  const body = (
    <div className="card-body">
      <div style={styles.top}>
        <div>
          <div style={styles.name}>{sensor.name}</div>
          {sensor.location && <div className="text-muted" style={styles.where}>{sensor.location}</div>}
        </div>
        <span style={styles.badge(st.tone)} data-testid="sensor-state">{st.label}</span>
      </div>
      <div style={styles.temp(st.tone)} className="mt-2" data-testid="sensor-temp">
        {fmtTemp(sensor.last_temp_f)}
      </div>
      {st.detail && <div style={styles.detail(st.tone)} data-testid="sensor-detail">{st.detail}</div>}
      <div style={styles.grid}>
        <span style={styles.dt}>Last reading</span><span data-testid="sensor-last">{lastReadingText(sensor, now)}</span>
        <span style={styles.dt}>Last 24 h</span><span data-testid="sensor-24h">{lowHigh}<span className="text-muted"> low / high</span></span>
        <span style={styles.dt}>Limits</span>
        <span data-testid="sensor-limits">
          {limitsText(sensor)}
          {settings && <span className="text-muted"> · {settings}</span>}
        </span>
      </div>
    </div>
  );
  if (!onSelect) {
    return <div className="card" style={styles.card(st.tone, false)} data-testid={`sensor-${sensor.sensor_id}`}>{body}</div>;
  }
  return (
    <button
      type="button"
      className="card p-0"
      style={styles.card(st.tone, selected)}
      onClick={() => onSelect(sensor.sensor_id)}
      aria-pressed={selected}
      aria-label={`${sensor.name}: ${fmtTemp(sensor.last_temp_f)}, ${st.label}. Show its chart.`}
      data-testid={`sensor-${sensor.sensor_id}`}
    >
      {body}
    </button>
  );
}
